/**
 * FairPlay (Safari / iOS) DRM on the native-HLS path.
 *
 * Safari plays HLS natively (no hls.js), so hls.js's EME controller never runs.
 * We drive EME directly on the <video> element with the standard `encrypted`
 * flow: fetch the server certificate, create a `com.apple.fps` MediaKeys,
 * generate an SPC request, POST it to the license server, and update the session
 * with the returned CKC.
 *
 * Very old Safari (webkit-prefixed `webkitneedkey` / `com.apple.fps.1_0`) is not
 * covered here; modern Safari (14+) uses the standard EME API below.
 */
export interface FairPlayConfig {
  licenseUrl: string;
  certificateUrl: string;
  /** Customize the license (CKC) request — add auth headers/body. */
  licenseXhrSetup?: (xhr: XMLHttpRequest, url: string) => void | Promise<void>;
}

/** Attach FairPlay handling to a native-HLS <video>. Returns a cleanup function. */
export function setupFairPlay(video: HTMLVideoElement, cfg: FairPlayConfig): () => void {
  const abort = new AbortController();
  let certPromise: Promise<ArrayBuffer> | null = null;
  const getCert = () =>
    (certPromise ??= fetch(cfg.certificateUrl, { signal: abort.signal }).then((r) => {
      if (!r.ok) throw new Error(`FairPlay certificate ${r.status}`);
      return r.arrayBuffer();
    }));

  const onEncrypted = async (ev: MediaEncryptedEvent) => {
    try {
      const cert = await getCert();
      const initDataType = ev.initDataType || "sinf";
      const access = await navigator.requestMediaKeySystemAccess("com.apple.fps", [{
        initDataTypes: [initDataType],
        videoCapabilities: [{ contentType: 'video/mp4; codecs="avc1.42E01E"' }],
        audioCapabilities: [{ contentType: 'audio/mp4; codecs="mp4a.40.2"' }],
        distinctiveIdentifier: "not-allowed",
        persistentState: "not-allowed",
        sessionTypes: ["temporary"],
      }]);
      const mediaKeys = await access.createMediaKeys();
      await mediaKeys.setServerCertificate(new Uint8Array(cert));
      await video.setMediaKeys(mediaKeys);

      const session = mediaKeys.createSession();
      session.addEventListener("message", (msg: MediaKeyMessageEvent) => {
        void requestLicense(cfg, msg.message)
          .then((ckc) => session.update(ckc))
          .catch((e) => console.error("[fairplay] license/update failed", e));
      });
      if (ev.initData) await session.generateRequest(initDataType, ev.initData);
    } catch (e) {
      console.error("[fairplay] setup failed", e);
    }
  };

  video.addEventListener("encrypted", onEncrypted);
  return () => {
    abort.abort();
    video.removeEventListener("encrypted", onEncrypted);
  };
}

/** POST the SPC to the FairPlay license server and return the CKC bytes. */
function requestLicense(cfg: FairPlayConfig, spc: ArrayBuffer): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", cfg.licenseUrl, true);
    xhr.responseType = "arraybuffer";
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    Promise.resolve(cfg.licenseXhrSetup?.(xhr, cfg.licenseUrl))
      .then(() => {
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve(xhr.response as ArrayBuffer)
            : reject(new Error(`FairPlay license ${xhr.status}`));
        xhr.onerror = () => reject(new Error("FairPlay license network error"));
        xhr.send(new Uint8Array(spc));
      })
      .catch(reject);
  });
}
